import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money } from "../lib/data";
import { Bell, Gift, Copy, Check, Vote, CheckCircle2, DollarSign, Award, Trophy, CalendarDays } from "lucide-react";

const FRI_FMT = { weekday: "short", month: "short", day: "numeric", year: "numeric" };

function fridayForWeek(approvedAt, w) {
  const d = new Date(approvedAt);
  const add = (5 - d.getDay() + 7) % 7; // next Friday (Fri=5)
  const fri = new Date(d);
  fri.setDate(d.getDate() + add + 7 * (w - 1));
  fri.setHours(16, 0, 0, 0);
  return fri;
}

export function LeaderboardPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/referrals/leaderboard").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (rows.length === 0) return null;
  const medal = (i) => ["#D4AF37", "#9CA3AF", "#B45309"][i] || "#0A1914";
  return (
    <div className="border border-gray-200 bg-white p-5" data-testid="leaderboard-panel">
      <div className="flex items-center gap-2 font-semibold text-[#0A1914] mb-1"><Trophy className="h-4 w-4 text-[#D4AF37]" /> Top Referrers</div>
      <p className="text-sm text-gray-600 mb-4">The members earning the most by spreading the word. Climb the ranks!</p>
      <div className="divide-y divide-gray-100">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between py-2.5" data-testid={`leader-row-${i}`}>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm w-6 text-center font-semibold" style={{ color: medal(i) }}>#{i + 1}</span>
              <div>
                <p className="text-sm font-medium text-[#0A1914]">{r.name}</p>
                <p className="text-xs text-gray-500">{r.referred_users} friend{r.referred_users !== 1 ? "s" : ""} · {r.count} bonus{r.count !== 1 ? "es" : ""}</p>
              </div>
            </div>
            <span className="font-mono text-[#00B368]">{money(r.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PayoutCalendar({ purchases }) {
  const approved = (purchases || []).filter((p) => p.status === "approved" && p.approved_at);
  const now = new Date();
  const map = {};
  approved.forEach((p) => {
    const paid = new Set(p.paid_weeks || []);
    for (let w = 1; w <= 52; w++) {
      const fri = fridayForWeek(p.approved_at, w);
      const key = fri.toISOString().slice(0, 10);
      if (!map[key]) map[key] = { date: fri, amount: 0, paid: 0, entries: [] };
      map[key].amount += p.weekly;
      if (paid.has(w)) map[key].paid += p.weekly;
      map[key].entries.push({ course: p.course_title, week: w, amount: p.weekly, isPaid: paid.has(w) });
    }
  });
  let days = Object.values(map).sort((a, b) => a.date - b.date);
  // show from the most recent past Friday onward, first 16 Fridays
  const upcomingIdx = days.findIndex((d) => d.date >= now);
  const start = Math.max(0, (upcomingIdx === -1 ? days.length : upcomingIdx) - 2);
  days = days.slice(start, start + 16);

  if (approved.length === 0) return null;

  const status = (d) => {
    if (d.paid >= d.amount && d.amount > 0) return { label: "Paid", cls: "text-[#00B368] border-[#00B368]/40 bg-[#00B368]/10" };
    if (d.paid > 0) return { label: "Partial", cls: "text-[#3B82F6] border-[#3B82F6]/40 bg-[#3B82F6]/10" };
    if (d.date < now) return { label: "Processing", cls: "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10" };
    return { label: "Upcoming", cls: "text-gray-500 border-gray-300 bg-gray-50" };
  };

  return (
    <div className="border border-gray-200 bg-white p-5" data-testid="payout-calendar">
      <div className="flex items-center gap-2 font-semibold text-[#0A1914] mb-1"><CalendarDays className="h-4 w-4 text-[#00B368]" /> Payout Calendar</div>
      <p className="text-sm text-gray-600 mb-4">Your weekly payments land every Friday before 4pm EST across all your active courses.</p>
      <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
        {days.map((d, i) => {
          const st = status(d);
          const isNext = d.date >= now && (i === 0 || days[i - 1].date < now);
          return (
            <div key={i} className={`flex items-center justify-between py-2.5 px-2 ${isNext ? "bg-[#00B368]/5" : ""}`} data-testid={`payout-friday-${d.date.toISOString().slice(0,10)}`}>
              <div>
                <p className="text-sm font-medium text-[#0A1914]">{d.date.toLocaleDateString("en-US", FRI_FMT)}{isNext && <span className="ml-2 text-xs font-mono text-[#00B368]">NEXT</span>}</p>
                <p className="text-xs text-gray-500">{d.entries.length} payment{d.entries.length !== 1 ? "s" : ""}: {d.entries.map((e) => e.course.split(" ")[0]).join(", ")}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[#0A1914]">{money(d.amount)}</span>
                <span className={`text-xs font-mono uppercase px-2 py-0.5 border rounded-sm ${st.cls}`}>{st.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function NotificationsPanel() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const load = () => api.get("/notifications/me").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markRead = async () => { await api.post("/notifications/read-all"); load(); };

  if (data.items.length === 0) return null;
  const icon = (t) => t === "payout" ? DollarSign : t === "referral" ? Gift : t === "approval" ? CheckCircle2 : Bell;

  return (
    <div className="border border-gray-200 bg-white mb-10" data-testid="notifications-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2 font-semibold text-[#0A1914]">
          <Bell className="h-4 w-4 text-[#00B368]" /> Notifications
          {data.unread > 0 && <span className="bg-[#00B368] text-white text-xs font-mono px-2 py-0.5 rounded-full" data-testid="unread-count">{data.unread}</span>}
        </div>
        {data.unread > 0 && <button onClick={markRead} data-testid="mark-read" className="text-xs text-gray-500 hover:text-[#0A1914] transition-colors">Mark all read</button>}
      </div>
      <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
        {data.items.slice(0, 8).map((n) => {
          const Icon = icon(n.type);
          return (
            <div key={n.id} className={`flex items-start gap-3 px-5 py-3 ${n.read ? "opacity-60" : ""}`}>
              <Icon className="h-4 w-4 text-[#00B368] mt-0.5 shrink-0" />
              <p className="text-sm text-gray-700">{n.message}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const link = `${window.location.origin}/register?ref=${data.code}`;
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); toast.success("Referral link copied"); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="border border-gray-200 bg-white p-5 mb-10" data-testid="referral-card">
      <div className="flex items-center gap-2 font-semibold text-[#0A1914] mb-1"><Gift className="h-4 w-4 text-[#00B368]" /> Refer &amp; earn 5%</div>
      <p className="text-sm text-gray-600 mb-4">Share your link. When a friend joins and their course purchase is approved, you earn <span className="font-semibold text-[#00B368]">5% of the course price</span>.</p>
      <div className="flex items-center gap-2 mb-4">
        <code className="flex-1 font-mono text-xs bg-[#F3F4F3] border border-gray-200 px-3 py-2.5 break-all" data-testid="referral-link">{link}</code>
        <button onClick={copy} data-testid="copy-referral" className="shrink-0 border border-[#0A1914] p-2.5 rounded-sm hover:bg-[#F3F4F3] transition-colors">
          {copied ? <Check className="h-4 w-4 text-[#00B368]" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-[#F9F9F8] border border-gray-200 px-3 py-2"><p className="text-xs text-gray-500">Friends joined</p><p className="font-mono text-lg text-[#0A1914]">{data.referred_users}</p></div>
        <div className="bg-[#F9F9F8] border border-gray-200 px-3 py-2"><p className="text-xs text-gray-500">Bonuses</p><p className="font-mono text-lg text-[#0A1914]">{data.count}</p></div>
        <div className="bg-[#F9F9F8] border border-gray-200 px-3 py-2"><p className="text-xs text-gray-500">Total earned</p><p className="font-mono text-lg text-[#00B368]" data-testid="referral-total">{money(data.total)}</p></div>
      </div>
      {data.items.length > 0 && (
        <div className="divide-y divide-gray-100 border-t border-gray-200">
          {data.items.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-2 text-sm">
              <span className="text-gray-600">{r.referred_email} · {r.course_title}</span>
              <span className="font-mono text-[#00B368]">+{money(r.bonus)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function VotingPanel() {
  const [data, setData] = useState({ eligible: false, proposals: [] });
  const load = () => api.get("/proposals").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const vote = async (pid, choice) => {
    try { await api.post(`/proposals/${pid}/vote`, { choice }); toast.success("Vote recorded"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not vote"); }
  };

  if (data.proposals.length === 0) return (
    <div className="border border-dashed border-gray-300 p-8 text-center text-gray-500" data-testid="no-proposals">
      No active proposals right now. As a course owner, you're part of our governance — check back soon.
    </div>
  );

  return (
    <div className="space-y-4" data-testid="voting-panel">
      {!data.eligible && <p className="text-sm text-gray-500">Purchase and get approved for a course to unlock voting — our paying members are the CEO.</p>}
      {data.proposals.map((p) => {
        const total = p.yes + p.no;
        const yesPct = total ? Math.round((p.yes / total) * 100) : 0;
        const noPct = total ? 100 - yesPct : 0;
        const closed = p.status === "closed";
        return (
          <div key={p.id} className="border border-gray-200 bg-white p-5" data-testid={`proposal-${p.id}`}>
            <div className="flex items-start justify-between gap-4 mb-1">
              <h3 className="font-display text-xl text-[#0A1914]">{p.title}</h3>
              <span className={`text-xs font-mono uppercase px-2 py-0.5 border rounded-sm shrink-0 ${closed ? "text-gray-500 border-gray-300 bg-gray-50" : "text-[#00B368] border-[#00B368]/40 bg-[#00B368]/10"}`}>{closed ? "Closed" : "Open"}</span>
            </div>
            {p.description && <p className="text-sm text-gray-600 mb-4">{p.description}</p>}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-3"><span className="w-10 text-xs font-mono text-[#00B368]">Yes</span><div className="flex-1 h-3 bg-gray-100"><div className="h-3 bg-[#00B368]" style={{ width: `${yesPct}%` }} /></div><span className="w-16 text-right text-xs font-mono text-gray-600">{p.yes} · {yesPct}%</span></div>
              <div className="flex items-center gap-3"><span className="w-10 text-xs font-mono text-red-500">No</span><div className="flex-1 h-3 bg-gray-100"><div className="h-3 bg-red-400" style={{ width: `${noPct}%` }} /></div><span className="w-16 text-right text-xs font-mono text-gray-600">{p.no} · {noPct}%</span></div>
            </div>
            {data.eligible && !closed ? (
              <div className="flex gap-2">
                <button onClick={() => vote(p.id, "yes")} data-testid={`vote-yes-${p.id}`} className={`flex items-center gap-1 text-sm px-4 py-2 rounded-sm transition-colors ${p.my_choice === "yes" ? "bg-[#00B368] text-white" : "border border-[#0A1914] hover:bg-[#F3F4F3]"}`}><Vote className="h-4 w-4" /> Vote Yes</button>
                <button onClick={() => vote(p.id, "no")} data-testid={`vote-no-${p.id}`} className={`flex items-center gap-1 text-sm px-4 py-2 rounded-sm transition-colors ${p.my_choice === "no" ? "bg-red-500 text-white" : "border border-[#0A1914] hover:bg-[#F3F4F3]"}`}><Vote className="h-4 w-4" /> Vote No</button>
              </div>
            ) : (
              <p className="text-xs text-gray-500 flex items-center gap-1"><Award className="h-3.5 w-3.5" /> {closed ? "Voting has closed." : p.my_choice ? `You voted ${p.my_choice}.` : "Owners only."}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
